/**
 * "How should the family be told?" — the one notification choice in the
 * Canada Post label dialog (T-114).
 *
 * It replaced a checkbox that chose WHEN to send, not WHETHER: unticked, the
 * dialog's blue button became "Send now", and on case 728 a coordinator who
 * unticked it to keep quiet texted the family the moment the label printed.
 * Now every outcome, including saying nothing, is an explicit choice that the
 * server applies and records on the case feed with the staff member's name.
 */

export type LabelNotifyChoice = 'on_scan' | 'now' | 'none';

export const DEFAULT_LABEL_NOTIFY_CHOICE: LabelNotifyChoice = 'on_scan';

export const LABEL_NOTIFY_OPTIONS: {
  value: LabelNotifyChoice;
  label: string;
  helper: string;
}[] = [
  {
    value: 'on_scan',
    label: 'Text + email when Canada Post scans it',
    helper:
      'Recommended. Sent automatically at the first scan, once the tracking link shows the remains on their way.',
  },
  {
    value: 'now',
    label: 'Text + email now',
    helper:
      'Sent as soon as the label is created. Tracking will read "Label Created" until Canada Post scans it.',
  },
  {
    value: 'none',
    label: "Don't notify the family",
    helper:
      'Nothing is sent now or later. The case feed records that the family was not notified.',
  },
];

/** The dialog's single primary button repeats the choice, so it can't surprise. */
export function createLabelButtonText(choice: LabelNotifyChoice): string {
  switch (choice) {
    case 'on_scan':
      return 'Create label · notify at first scan';
    case 'now':
      return 'Create label · notify now';
    case 'none':
      return "Create label · don't notify";
  }
}

/**
 * Saved with every new tracking number (and on void), so nothing queued or
 * sent for one label carries over to the next: the old text can't go out with
 * the old number, and "don't notify" on a new label can't be overridden by an
 * earlier queue.
 */
export const SHIPPING_NOTIFY_RESET = {
  shipping_notify_on_ship: false,
  shipping_notify_body: null,
  shipping_notify_sent_at: null,
  shipping_notify_emailed_pin: null,
} as const;

export type LabelNotifyResult =
  | 'queued'
  | 'sent'
  | 'partial'
  | 'already_sent'
  | 'not_notified'
  | 'no_contact'
  | 'failed'
  | 'stale';

/** What staff see after the label is created: a severity and one sentence. */
export function describeNotifyOutcome(result: LabelNotifyResult | null): {
  severity: 'success' | 'info' | 'warning' | 'error';
  text: string;
} {
  switch (result) {
    case 'queued':
      return {
        severity: 'success',
        text: 'The family will be texted and emailed at the first Canada Post scan.',
      };
    case 'sent':
      return {
        severity: 'success',
        text: 'The family was sent the tracking number.',
      };
    case 'partial':
      return {
        severity: 'warning',
        text: 'The tracking number reached the family on one channel only. See the case feed.',
      };
    case 'already_sent':
      return {
        severity: 'info',
        text: 'The family already has this tracking number.',
      };
    case 'not_notified':
      return { severity: 'info', text: 'The family was not notified.' };
    case 'no_contact':
      return {
        severity: 'warning',
        text: 'The family was not notified: no phone number or email on file.',
      };
    case 'failed':
      return {
        severity: 'error',
        text: 'The tracking number could not be sent to the family. Send it from the case feed.',
      };
    case 'stale':
    case null:
    default:
      return {
        severity: 'warning',
        text: 'The family was not notified: the notification could not be saved. Copy the tracking number and send it from the case feed.',
      };
  }
}
